import React, { useState, useMemo, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { logout as logoutAction } from '../store/slices/authSlice';
import { toastSuccess } from '../utils/toast';
import {
    LayoutDashboard,
    Box,
    Settings,
    User,
    LogOut,
    Database,
    ImagePlus,
    ChevronLeft,
    ChevronRight,
    Sparkles,
    Sun,
    Moon
} from 'lucide-react';

const Layout = () => {
    const { logout: contextLogout, guestSessionExpiry, settings, setSettings, user: contextUser } = useApp();
    const isDark = (settings?.theme || 'dark') === 'dark';
    const dispatch = useAppDispatch();
    const reduxUser = useAppSelector((state) => state.auth.user);
    const authUser = reduxUser || contextUser;
    const navigate = useNavigate();
    const location = useLocation();
    const [guestTimeLeft, setGuestTimeLeft] = useState(null);

    const isGuest = authUser?.role === 'guest' || authUser == null;
    const isSuperAdmin = authUser?.role === 'superAdmin';
    const isAdminOrSuper = isSuperAdmin || authUser?.role === 'admin';

    useEffect(() => {
        if (!isGuest || !guestSessionExpiry) {
            setGuestTimeLeft(null);
            return;
        }
        const tick = () => {
            const left = Math.max(0, guestSessionExpiry - Date.now());
            setGuestTimeLeft(left);
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [isGuest, guestSessionExpiry]);

    const handleLogout = () => {
        try {
            localStorage.clear();
            sessionStorage.clear();
        } catch (_) { }
        dispatch(logoutAction());
        contextLogout();
        toastSuccess(`Signed out. See you soon!`);
        navigate('/login', { replace: true });
    };

    const handleThemeToggle = (targetTheme) => {
        const currentTheme = settings?.theme || 'dark';
        const nextTheme = targetTheme
            ? (targetTheme === currentTheme ? (targetTheme === 'dark' ? 'light' : 'dark') : targetTheme)
            : (isDark ? 'light' : 'dark');
        setSettings((prev) => ({ ...prev, theme: nextTheme }));
        toastSuccess(`Switched to ${nextTheme === 'dark' ? 'Dark' : 'Light'} Mode`);
    };

    const navItems = useMemo(() => {
        const items = [
            { path: '/room-canvas', label: 'Room Canvas' },
            { path: '/3d', label: '3D' },
        ];
        if (isAdminOrSuper) {
            items.unshift({ path: '/dashboard', label: 'Dashboard' });
        }
        if (isAdminOrSuper) {
            items.push({ path: '/admin', label: 'Admin' });
            items.push({ path: '/settings', label: 'Settings' });
        }
        return items;
    }, [isAdminOrSuper, isSuperAdmin]);

    const isRoomCanvas = location.pathname === '/room-canvas';

    return (
        <div className="app-container" data-theme={settings?.theme || 'dark'}>
            <div className="app-bg" aria-hidden="true" />

            {authUser && (
                <header className="global-header" style={{ height: '70px', minHeight: '70px', padding: '0 1.5rem', background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                    <div className="header-left" style={{ gap: '2rem' }}>
                        <div className="logo">
                            <Link to="/dashboard" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }} aria-label="Tileverz">
                                <div className="marketing-logo-container" style={{ height: '58px', width: '200px' }}>
                                    <img
                                        src="/logo.png"
                                        alt="Tileverz"
                                        className="marketing-logo-img"
                                    />
                                </div>
                            </Link>
                        </div>
                        <nav className="header-nav">
                            {navItems.map(item => (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                        </nav>
                    </div>
                    <div className="header-right">
                        {isGuest && guestTimeLeft != null && (
                            <div style={{
                                padding: '6px 12px',
                                background: isDark
                                    ? (guestTimeLeft <= 60000 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)')
                                    : (guestTimeLeft <= 60000 ? '#fef2f2' : '#f0fdf4'),
                                borderRadius: '8px',
                                fontSize: '12px',
                                fontWeight: '700',
                                color: isDark
                                    ? (guestTimeLeft <= 60000 ? '#f87171' : '#34d399')
                                    : (guestTimeLeft <= 60000 ? '#991b1b' : '#166534'),
                            }}>
                                {guestTimeLeft > 0
                                    ? `${Math.floor(guestTimeLeft / 60000)}:${String(Math.floor((guestTimeLeft % 60000) / 1000)).padStart(2, '0')} left`
                                    : 'Session ended'}
                            </div>
                        )}
                        <div className="theme-toggle-pill" role="group" aria-label="Theme mode toggle">
                            <button
                                type="button"
                                onClick={() => handleThemeToggle('light')}
                                className={`theme-toggle-btn ${!isDark ? 'active sun-active' : ''}`}
                                title="Switch to Light Mode"
                                aria-label="Switch to Light Mode"
                                aria-pressed={!isDark}
                            >
                                <Sun size={15} />
                                <span>Light</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => handleThemeToggle('dark')}
                                className={`theme-toggle-btn ${isDark ? 'active moon-active' : ''}`}
                                title="Switch to Dark Mode"
                                aria-label="Switch to Dark Mode"
                                aria-pressed={isDark}
                            >
                                <Moon size={15} />
                                <span>Dark</span>
                            </button>
                        </div>
                        <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <User size={18} />
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
                                <span style={{ fontWeight: 600 }}>{authUser?.name || 'User'}</span>
                                {isSuperAdmin && (
                                    <span style={{ fontSize: '0.68rem', color: '#0284c7', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        Platform Super Admin
                                    </span>
                                )}
                                {authUser?.role === 'admin' && (
                                    <span style={{ fontSize: '0.68rem', color: '#7c3aed', fontWeight: 700 }}>
                                        {typeof authUser?.organizationId === 'object' && authUser?.organizationId?.name
                                            ? `${authUser.organizationId.name} • Admin`
                                            : 'Org Admin'}
                                    </span>
                                )}
                                {authUser?.role === 'executive' && (
                                    <span style={{ fontSize: '0.68rem', color: '#059669', fontWeight: 700 }}>
                                        {typeof authUser?.organizationId === 'object' && authUser?.organizationId?.name
                                            ? `${authUser.organizationId.name} • Executive`
                                            : 'Executive'}
                                    </span>
                                )}
                            </div>
                        </div>
                        <button onClick={handleLogout} className="logout-header-btn">
                            <LogOut size={18} />
                            <span>Logout</span>
                        </button>
                    </div>
                </header>
            )}

            <main className="main-content">
                <div className="main-content-inner">
                    <Outlet />
                </div>
            </main>

            <style>{`
                .app-container {
                    display: flex;
                    flex-direction: column;
                    height: 100vh;
                    width: 100vw;
                    background: var(--bg-muted, #f1f5f9);
                    color: var(--text, #0f172a);
                    font-family: 'Inter', sans-serif;
                    overflow: hidden;
                }
                .global-header {
                    height: 70px;
                    min-height: 70px;
                    background: #ffffff;
                    border-bottom: 1px solid #e2e8f0;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0 1.5rem;
                    z-index: 100;
                    flex-shrink: 0;
                }
                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 1.75rem;
                }
                .logo h2 {
                    margin: 0;
                    letter-spacing: -0.025em;
                    font-size: 1.2rem;
                    font-weight: 900;
                    font-family: 'DM Sans', system-ui, sans-serif;
                }
                .logo h2 .logo-tile {
                    color: var(--text, #0f172a);
                }
                .logo h2 .logo-verz {
                    color: #000F45;
                }
                .header-nav {
                    display: flex;
                    align-items: center;
                    gap: 1.5rem;
                }
                .nav-link {
                    font-size: 0.9rem;
                    font-weight: 600;
                    color: var(--text-muted, #64748b);
                    text-decoration: none;
                    transition: all 0.2s;
                    padding: 0.4rem 0;
                    border-bottom: 2px solid transparent;
                }
                .nav-link:hover {
                    color: var(--text, #0f172a);
                }
                .nav-link.active {
                    color: #000F45;
                    border-bottom-color: #000F45;
                }
                .header-right {
                    display: flex;
                    align-items: center;
                    gap: 1.25rem;
                }
                .user-info {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    color: var(--text-muted, #64748b);
                    font-size: 0.85rem;
                    font-weight: 500;
                }
                .theme-toggle-pill {
                    display: inline-flex;
                    align-items: center;
                    background: #f1f5f9;
                    border: 1px solid #e2e8f0;
                    border-radius: 9999px;
                    padding: 3px;
                    gap: 2px;
                    box-sizing: border-box;
                }
                .theme-toggle-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    padding: 4px 10px;
                    border-radius: 9999px;
                    border: none;
                    background: transparent;
                    color: #64748b;
                    font-size: 0.8rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    user-select: none;
                    outline: none;
                }
                .theme-toggle-btn:hover:not(.active) {
                    color: #0f172a;
                    background: rgba(0, 15, 69, 0.04);
                }
                .theme-toggle-btn.active {
                    background: #ffffff;
                    font-weight: 700;
                    box-shadow: 0 1px 3px rgba(0, 15, 69, 0.12), 0 1px 2px rgba(0, 0, 0, 0.06);
                }
                .theme-toggle-btn.active.sun-active {
                    color: #b45309;
                }
                .theme-toggle-btn.active.moon-active {
                    color: #000F45;
                }
                .logout-header-btn {
                    display: flex;
                    align-items: center;
                    gap: 0.4rem;
                    padding: 0.5rem 0.75rem;
                    background: transparent;
                    border: 1px solid var(--border, #e2e8f0);
                    color: #64748b;
                    border-radius: 0.5rem;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 0.875rem;
                    font-weight: 600;
                }
                .logout-header-btn:hover {
                    background: rgba(0, 15, 69, 0.06);
                    color: #000F45;
                    border-color: #000F45;
                }
                .main-content {
                    flex: 1;
                    min-height: 0;
                    min-width: 0;
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    position: relative;
                    background: var(--bg-subtle, #f8fafc);
                    overflow: hidden;
                }
                .main-content-inner {
                    flex: 1;
                    min-height: 0;
                    min-width: 0;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }
                .main-content-inner > * {
                    flex: 1;
                    min-height: 0;
                    min-width: 0;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }
                @media (max-width: 768px) {
                    .global-header {
                        padding: 0 0.75rem;
                    }
                    .header-left {
                        gap: 1rem;
                    }
                    .header-nav {
                        gap: 0.75rem;
                        overflow-x: auto;
                        scrollbar-width: none;
                        -ms-overflow-style: none;
                    }
                    .header-nav::-webkit-scrollbar {
                        display: none;
                    }
                    .nav-link {
                        font-size: 0.825rem;
                        white-space: nowrap;
                    }
                    .header-right {
                        gap: 0.5rem;
                    }
                    .user-info {
                        font-size: 0.75rem;
                    }
                }
                @media (max-width: 640px) {
                    .theme-toggle-btn span {
                        display: none;
                    }
                    .theme-toggle-btn {
                        padding: 4px 6px;
                    }
                }
                @media (max-width: 480px) {
                    .global-header {
                        padding: 0 0.5rem;
                    }
                    .logo h2 {
                        font-size: 1.05rem;
                    }
                    .user-info span {
                        display: none;
                    }
                    .logout-header-btn span {
                        display: none;
                    }
                    .logout-header-btn {
                        padding: 0.4rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default Layout;
